'use client'

import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { WizardPanel } from '@repo/ui/WizardPanel'
import { WizardIconTitle } from '@repo/ui/WizardIconTitle'
import { WizardFormSection } from '@repo/ui/WizardFormSection'
import { WizardFooter } from '@repo/ui/WizardFooter'
import { useNewMatterStore } from '@repo/store/useNewMatterStore'
import { useNewClientStore } from '@repo/store/useNewClientStore'
import { LargeWalletIcon } from '@repo/ui/LargeWalletIcon'
import { RemunerationSelect } from '@repo/ui/RemunerationSelect'
import { FranchiseCategorySelect } from '@repo/ui/FranchiseCategorySelect'
import { LegalAidVersionSelect } from '@repo/ui/LegalAidVersionSelect'
import { validate } from '@repo/validation/validation'
import { matterFundingSchema } from '@repo/validation/schema_NewMatter'
import { useToastStore } from '@repo/store/useToastStore'
import { DateSelection } from '@repo/ui/DateSelection'
import { NewUFNEntry } from '@repo/ui/NewUFNEntry'
import MUIStack from '@mui/material/Stack'
import MUIRadio from '@mui/material/Radio'
import MUIRadioGroup from '@mui/material/RadioGroup'
import MUIFormControlLabel from '@mui/material/FormControlLabel'

export const NewMatterFunding = ({ updateStepState, wideMode, handlePrevNext, testData }) => {
  const formRef = useRef()
  const {
    privateOrLegalAid,
    remunerationType,
    franchiseCategory,
    uniqueFileNoDate,
    crmSchemeId,
    crmScheme,
    updateState
  } = useNewMatterStore()

  const { newClientNo, existingClient } = useNewClientStore()

  const [validationErrors, setValidationErrors] = useState({})
  const [ufnError, setUfnError] = useState(null)
  const { showToast } = useToastStore()

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (ufnError) return

    setValidationErrors({})

    const stateToSave = {
      privateOrLegalAid,
      remunerationType
    }

    try {
      await validate(matterFundingSchema, stateToSave)
    } catch (err) {
      const { inner } = err
      const errors = {}
      let firstErrorKey
      inner.forEach(({ path, message }, index) => {
        errors[path] = message
        if (index === 0) firstErrorKey = CSS.escape(path)
      })

      console.log(errors)

      showToast({ type: 'error', text: 'Please check all fields for errors', title: 'Form errors' })

      // scroll to error if possible
      const container = document.querySelector('[data-testid="WizardStepContentsContainer"]')
      const errorElement = firstErrorKey
        ? document.querySelector(`[data-testid="WizardPanel"]:has([name=${firstErrorKey}])`)
        : null
      if (container && errorElement) container.scrollTop = errorElement.offsetParent?.offsetTop || 0

      setValidationErrors(errors)
      return
    }

    updateStepState({ status: 'completed', statusText: 'Complete' })
    handlePrevNext(1)
  }

  const handleClickBack = () => {
    handlePrevNext(-1)
  }

  useEffect(() => {
    if (privateOrLegalAid === 'P') {
      if (franchiseCategory) updateState({ key: 'franchiseCategory', value: null })
      if (crmScheme) updateState({ key: 'crmScheme', value: null })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privateOrLegalAid])

  useEffect(() => {
    const wrongPrviateOrLegalAidInremunerationType =
      remunerationType && privateOrLegalAid !== remunerationType.privateOrLegalAid

    const schemeId = crmScheme?.schemeId || crmSchemeId || 0
    const wrongCrmSchemeInRemunerationType = remunerationType && schemeId !== remunerationType.crmSchemeId

    if (wrongPrviateOrLegalAidInremunerationType || wrongCrmSchemeInRemunerationType) {
      updateState({ key: 'remunerationType', value: null })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privateOrLegalAid, crmScheme])

  const dateToUse = uniqueFileNoDate || new Date()
  const date = dateToUse && typeof dateToUse === 'string' ? new Date(dateToUse) : dateToUse

  return (
    <form onSubmit={handleSubmit} ref={formRef}>
      <WizardPanel data-testid="NewMatterFunding">
        <WizardFormSection>
          <WizardIconTitle title={'Funding'} icon={LargeWalletIcon} sx={{ mb: 4, width: '100%' }} />

          <MUIRadioGroup
            value={privateOrLegalAid}
            name="privateOrLegalAid"
            row
            sx={{ width: '100%', mb: 2 }}
            onChange={(e) => updateState({ key: 'privateOrLegalAid', value: e.target.value })}
          >
            <MUIFormControlLabel value="P" control={<MUIRadio />} label="Private" />
            <MUIFormControlLabel value="L" control={<MUIRadio />} label="Legal Aid" />
          </MUIRadioGroup>

          {privateOrLegalAid === 'L' ? (
            <>
              <FranchiseCategorySelect
                size="medium"
                value={franchiseCategory}
                onChange={(value) => updateState({ key: 'franchiseCategory', value })}
                sx={{ width: '100%', maxWidth: '486px' }}
                errors={validationErrors}
                testData={testData}
                required
              />

              {franchiseCategory?.franchiseTypeId === 'CRI' || franchiseCategory?.franchiseTypeId === 'CIV' ? (
                <>
                  <MUIStack direction="row" spacing={4} sx={{ width: '100%', maxWidth: 'calc(486px + 55px)' }}>
                    <DateSelection
                      format="d MMM yyyy"
                      value={date}
                      onChange={(value) => updateState({ key: 'uniqueFileNoDate', value })}
                      size="medium"
                      label="Unique file no date"
                      sx={{ flex: 1 }}
                      enableTypedDdMmYyyy={true}
                      showAdornment={true}
                      largeAdornment={true}
                    />
                    <NewUFNEntry
                      date={uniqueFileNoDate}
                      onChange={(value) => updateState({ key: 'uniqueFileNo', value })}
                      size="medium"
                      sx={{ width: '50%' }}
                      testMode={testData}
                      onError={setUfnError}
                      clientNo={newClientNo || existingClient?.clientNo || null}
                    />
                  </MUIStack>

                  <LegalAidVersionSelect
                    size="medium"
                    value={crmScheme}
                    comparisonDate={uniqueFileNoDate}
                    onChange={(value) => updateState({ key: 'crmScheme', value })}
                    sx={{ width: '100%', maxWidth: '486px' }}
                    errors={validationErrors}
                    testData={testData}
                  />
                </>
              ) : null}
            </>
          ) : null}

          <RemunerationSelect
            size="medium"
            crmSchemeId={crmScheme?.schemeId || crmSchemeId || 0}
            privateOrLegalAid={privateOrLegalAid || null}
            value={remunerationType}
            onChange={(value) => updateState({ key: 'remunerationType', value })}
            sx={{ width: '100%', maxWidth: '486px' }}
            errors={validationErrors}
            name="remunerationType"
            testData={testData}
            required
          />
        </WizardFormSection>
      </WizardPanel>
      <WizardFooter wideMode={wideMode} handleClickBack={handleClickBack} continueButtonIsFormSubmitButton />
    </form>
  )
}

NewMatterFunding.propTypes = {
  updateStepState: PropTypes.func,
  currentStep: PropTypes.object,
  wideMode: PropTypes.bool,
  handlePrevNext: PropTypes.func,
  testData: PropTypes.array
}
